import React, { useEffect, useState } from 'react';
import { post } from '../utils/api';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/shadcn/dialog';

// First-visit "request a call back" popup on the public landing page. Leads go through
// the existing /interest-registration rail (Mongo + Zapier -> Gmail), tagged by source.
export const STORAGE_KEY = 'nova_callback_popup_seen_at';
export const SHOW_DELAY_MS = 7000;
const SUPPRESS_MS = 30 * 24 * 60 * 60 * 1000;

// localStorage can throw (private mode, blocked site data); treat that as "never seen".
const seenRecently = () => {
  try {
    const seenAt = Number(localStorage.getItem(STORAGE_KEY));
    return seenAt > 0 && Date.now() - seenAt < SUPPRESS_MS;
  } catch {
    return false;
  }
};

const markSeen = () => {
  try {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // Without storage the popup may show again on a later visit; nothing else to do.
  }
};

const EMAIL_PATTERN = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const PHONE_PATTERN = /^\+?\d{7,20}$/;

const inputClass =
  'w-full h-12 rounded-xl border-2 border-[#E5E0F5] bg-white px-4 text-base text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#765AD8]';

const Field = ({ id, label, ...inputProps }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-semibold text-[#2F1E6B]">
      {label} <span className="text-[#765AD8]">*</span>
    </label>
    <input id={id} className={inputClass} required {...inputProps} />
  </div>
);

const CallbackPopup = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (seenRecently()) return;
    const timer = setTimeout(() => setOpen(true), SHOW_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  const handleOpenChange = (next) => {
    if (!next) markSeen();
    setOpen(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const cleanMobileNumber = mobileNumber.replace(/[\s\-()]/g, '');
    if (!trimmedName) return setError('Please enter your name');
    if (!PHONE_PATTERN.test(cleanMobileNumber)) return setError('Please enter a valid phone number');
    if (!EMAIL_PATTERN.test(trimmedEmail)) return setError('Please enter a valid email address');

    setSubmitting(true);
    try {
      await post('/interest-registration', {
        name: trimmedName,
        email: trimmedEmail,
        mobileNumber: cleanMobileNumber,
        source: 'landing-popup',
      });
      markSeen();
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="bg-white border-0 rounded-3xl sm:rounded-3xl max-md:rounded-3xl p-6 sm:p-10 gap-6 max-w-xl">
        {submitted ? (
          <div className="space-y-3 py-4 text-center">
            <DialogTitle className="text-2xl sm:text-3xl font-medium leading-tight text-[#2F1E6B]"
                         style={{ fontFamily: 'Fredoka, sans-serif' }}>
              Thank you!
            </DialogTitle>
            <DialogDescription className="text-base text-gray-700">
              We'll call you within 1 working day.
            </DialogDescription>
          </div>
        ) : (
          <>
            <div className="space-y-3 pr-6">
              <DialogTitle className="text-2xl sm:text-4xl font-medium leading-tight text-[#2F1E6B]"
                           style={{ fontFamily: 'Fredoka, sans-serif' }}>
                Have Questions About Your Child's Maths?
              </DialogTitle>
              <DialogDescription className="text-base text-gray-700">
                Leave your details and we'll call you within 1 working day to discuss your child's
                needs and answer any questions about our maths programmes.
              </DialogDescription>
            </div>

            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <Field id="callback-name" label="Parent Name" type="text" autoComplete="name"
                     placeholder="Enter your full name" maxLength={100}
                     value={name} onChange={(e) => setName(e.target.value)} />
              <Field id="callback-phone" label="Phone Number" type="tel" autoComplete="tel"
                     placeholder="Enter your phone number" maxLength={25}
                     value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} />
              <Field id="callback-email" label="Email" type="email" autoComplete="email"
                     placeholder="Enter your email address" maxLength={254}
                     value={email} onChange={(e) => setEmail(e.target.value)} />

              {error && <p role="alert" className="text-sm text-red-600">{error}</p>}

              <button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto h-12 px-6 rounded-3xl bg-[#2F1E6B] border-2 border-[#241553] text-white font-medium hover:bg-[#241553] disabled:opacity-60 transition-colors"
              >
                {submitting ? 'Sending…' : 'Request A Call Back →'}
              </button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CallbackPopup;
